import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X, ShieldCheck, BarChart3, Megaphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { hasDecided, saveConsent, readConsent, DEFAULT_CHOICE } from "../lib/consent";

const Row = ({ icon: Icon, title, desc, enabled, disabled, alwaysOn, onToggle, testId }) => (
  <div className="flex items-start gap-3 py-3 border-b border-zinc-100 last:border-b-0">
    <div className="w-9 h-9 rounded-lg bg-zinc-50 flex items-center justify-center flex-shrink-0">
      <Icon size={15} className="text-zinc-700" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <span className="font-bold text-zinc-950 text-sm">{title}</span>
        {disabled && <span className="text-[9px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{alwaysOn}</span>}
      </div>
      <p className="text-xs text-zinc-600 leading-relaxed">{desc}</p>
    </div>
    <label className={`relative inline-flex cursor-pointer mt-1 ${disabled ? "cursor-not-allowed opacity-60" : ""}`}>
      <input type="checkbox" checked={enabled} disabled={disabled}
        onChange={() => !disabled && onToggle?.(!enabled)}
        className="sr-only peer" data-testid={testId} />
      <div className="w-10 h-5 bg-zinc-200 rounded-full peer-checked:bg-zinc-950 transition-colors" />
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${enabled ? "translate-x-5" : ""}`} />
    </label>
  </div>
);

function Preferences({ initial, onSave, onCancel, saving }) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState(initial || DEFAULT_CHOICE);
  return (
    <div className="space-y-1" data-testid="consent-preferences">
      <Row icon={ShieldCheck} title={t("consent.category_strict")} desc={t("consent.category_strict_desc")} enabled={true} disabled alwaysOn={t("consent.always_on")} testId="consent-toggle-strict" />
      <Row icon={BarChart3} title={t("consent.category_analytics")} desc={t("consent.category_analytics_desc")} enabled={choice.analytics}
        onToggle={(v) => setChoice({ ...choice, analytics: v })} testId="consent-toggle-analytics" />
      <Row icon={Megaphone} title={t("consent.category_marketing")} desc={t("consent.category_marketing_desc")} enabled={choice.marketing}
        onToggle={(v) => setChoice({ ...choice, marketing: v })} testId="consent-toggle-marketing" />
      <div className="flex flex-wrap gap-2 mt-4 justify-end">
        <button onClick={onCancel} disabled={saving} data-testid="btn-consent-cancel"
          className="px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 rounded-full">{t("common.cancel")}</button>
        <button onClick={() => onSave({ analytics: false, marketing: false })} disabled={saving} data-testid="btn-consent-reject-all"
          className="px-4 py-2 text-sm font-semibold text-zinc-700 border border-zinc-200 rounded-full hover:bg-zinc-50">{t("consent.reject")}</button>
        <button onClick={() => onSave(choice)} disabled={saving} data-testid="btn-consent-save-choice"
          className="px-4 py-2 text-sm font-semibold text-white bg-zinc-700 rounded-full hover:bg-zinc-800">{t("consent.save_choice")}</button>
        <button onClick={() => onSave({ analytics: true, marketing: true })} disabled={saving} data-testid="btn-consent-accept-all"
          className="px-5 py-2 text-sm font-semibold text-white bg-zinc-950 rounded-full hover:bg-zinc-800">{t("consent.accept")}</button>
      </div>
    </div>
  );
}

/** Bottom-sticky banner shown until the user makes a choice. */
export default function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [saving, setSaving] = useState(false);

  // Suppress the consent banner inside the admin app — admins are
  // first-party operators, not visitors. The banner remains available on
  // every public-facing route.
  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => { if (!isAdminRoute && !hasDecided()) setShow(true); }, [isAdminRoute]);
  useEffect(() => {
    const handler = () => { setShowPrefs(true); setShow(true); };
    window.addEventListener("lf:open-cookie-settings", handler);
    return () => window.removeEventListener("lf:open-cookie-settings", handler);
  }, []);

  const save = async (choiceFromButtons) => {
    setSaving(true);
    try {
      await saveConsent(choiceFromButtons, showPrefs ? "preferences" : "banner");
      setShow(false); setShowPrefs(false);
    } finally { setSaving(false); }
  };

  if (!show || isAdminRoute) return null;
  const initial = readConsent()?.choice || DEFAULT_CHOICE;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5 bg-gradient-to-t from-black/40 to-transparent" data-testid="cookie-consent-banner">
      <div className="max-w-3xl mx-auto bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-zinc-950 flex items-center justify-center flex-shrink-0">
            <Cookie size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-zinc-950 text-base">{t("consent.title")}</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{t("consent.intro")}</p>
            <a
              href="/legal#gdpr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cookie-policy-link"
              className="text-xs text-zinc-500 underline hover:text-zinc-900 inline-block mt-2"
            >
              {t("consent.policy")}
            </a>
          </div>
          {!showPrefs && (
            <button onClick={() => { setShow(false); saveConsent({ analytics: false, marketing: false }, "dismiss"); }} data-testid="btn-consent-close"
              className="p-1 hover:bg-zinc-100 rounded-lg text-zinc-400" title={t("consent.reject")}><X size={14} /></button>
          )}
        </div>
        {!showPrefs ? (
          <div className="flex flex-wrap gap-2 justify-end mt-3">
            <button onClick={() => setShowPrefs(true)} data-testid="btn-consent-customize"
              className="px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 rounded-full">{t("consent.customize")}</button>
            <button onClick={() => save({ analytics: false, marketing: false })} disabled={saving} data-testid="btn-consent-reject-all-banner"
              className="px-4 py-2 text-sm font-semibold text-zinc-700 border border-zinc-200 rounded-full hover:bg-zinc-50">{t("consent.reject")}</button>
            <button onClick={() => save({ analytics: true, marketing: true })} disabled={saving} data-testid="btn-consent-accept-all-banner"
              className="px-5 py-2 text-sm font-semibold text-white bg-zinc-950 rounded-full hover:bg-zinc-800">{t("consent.accept")}</button>
          </div>
        ) : (
          <Preferences initial={initial} onSave={save} onCancel={() => setShowPrefs(false)} saving={saving} />
        )}
      </div>
    </div>
  );
}

export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent("lf:open-cookie-settings"));
}

